import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

import { getTodaysDate } from "@/lib/dayjs/get-todays-date";

import * as Constants from "../_constants";

export const useHandleSearchParams = () => {
	const defaultSearchParams = useMemo(() => {
		return {
			[Constants.SearchKey.searchDate]: getTodaysDate(),
			[Constants.FilterKey.optionId]: Constants.FilterValue.displayAll,
		};
	}, []);
	const [searchParams, setSearchParams] = useSearchParams(defaultSearchParams);

	/**
	 * 検索日
	 */
	const searchDate = useMemo(
		() => searchParams.get(Constants.SearchKey.searchDate) || getTodaysDate(),
		[searchParams],
	);

	/**
	 * カンマ区切りのオプション ID を配列へ変換
	 */
	const onFormatListForOptionId = useCallback((optionId: string | null) => {
		return optionId !== null ? optionId.split(",").filter((value) => value.trim().length > 0) : [];
	}, []);
	/**
	 * オプション ID をカンマ区切りへ変換
	 */
	const onFormatWithForCommaOptionId = useCallback((optionIdList: Array<string>) => {
		return optionIdList.join(",");
	}, []);
	/**
	 * オプション ID（表示するオプション）
	 */
	const optionIdList = useMemo((): Array<string> => {
		const optionId = searchParams.get(Constants.FilterKey.optionId);
		return onFormatListForOptionId(optionId);
	}, [onFormatListForOptionId, searchParams]);

	/**
	 * 検索日のセット
	 */
	const onSetSearchDate = useCallback(
		(date: string) => {
			const optionId = onFormatWithForCommaOptionId(optionIdList);

			/**
			 * query params に検索条件をセットする
			 */
			setSearchParams(
				(prev) => ({
					...prev,
					[Constants.SearchKey.searchDate]: date,
					[Constants.FilterKey.optionId]: optionId ? optionId : Constants.FilterValue.displayAll,
				}),
				{ preventScrollReset: true },
			);
		},
		[onFormatWithForCommaOptionId, optionIdList, setSearchParams],
	);
	/**
	 * オプション ID（表示するオプション）のセット
	 */
	const onSetOptionIdList = useCallback(
		(optionIdList: Array<string>) => {
			const _optionId = onFormatWithForCommaOptionId(optionIdList);

			setSearchParams(
				(prev) => ({
					...prev,
					[Constants.SearchKey.searchDate]: searchDate ?? "",
					[Constants.FilterKey.optionId]: _optionId,
				}),
				{ preventScrollReset: true },
			);
		},
		[onFormatWithForCommaOptionId, searchDate, setSearchParams],
	);

	return {
		// 検索日
		searchDate,
		// オプション ID（表示するオプション）
		optionIdList,
		//
		// 検索日をセット
		onSetSearchDate,
		// オプション ID（表示するオプション）をセット
		onSetOptionIdList,
		// カンマ区切りのオプション ID を配列へ変換
		onFormatListForOptionId,
		// オプション ID をカンマ区切りへ変換
		onFormatWithForCommaOptionId,
	};
};
